import React, { useEffect, useMemo, useState, useRef, useCallback, useLayoutEffect } from 'react';
import type { ProductDisplayData, ProductItem, CategoryGroup } from './types';
import ProductCardDetailed from './ProductCardDetailed';
import ProductFilterToolbar from './ProductFilterToolbar';

type SortMode = 'default' | 'collection' | 'price_low' | 'price_high' | 'weight_high' | 'weight_low';

interface ProductDisplayProps {
  data: ProductDisplayData;
  ProductCardComponent: React.ComponentType<{ product: ProductItem; scale?: number; activeColorFilter?: string | null }>;
  displayMode?: 'detailed' | 'standard'; // Explicit display mode (if not provided, auto-calculate)
  scale?: number; // Scale factor (0.7 - 1.0), default 1.0
  selectedStyleCodes?: string[];
  onSelectedStyleCodesChange?: (codes: string[]) => void;
  /**
   * Optional callback that fires whenever the post-facet/post-sort visible set
   * changes. Consumers (e.g. Collaborator Inquire) use this to send the user's
   * currently-curated products to downstream actions without re-running search.
   */
  onVisibleProductsChange?: (products: ProductItem[]) => void;
  /**
   * Optional "highlight" multi-select that paints a checkbox overlay on each
   * card and tracks the user's curated subset. Unlike `selectedStyleCodes`
   * (which filters the displayed list), highlight does NOT filter — it just
   * marks items so consumers can use the curated set for downstream actions
   * (e.g. Inquire only on the highlighted set).
   */
  highlightedStyleCodes?: string[];
  onHighlightedStyleCodesChange?: (codes: string[]) => void;
  /**
   * When true, render every product up-front and suppress the "Show More"
   * pagination control. Used by Collaborator mode, where the full result set
   * should always be visible for curation/inquiry.
   */
  showAllProducts?: boolean;
}

// Card width constants matching CSS grid: repeat(auto-fill, minmax(280px, 1fr))
const CARD_MIN_WIDTH_NORMAL = 280;
const CARD_MIN_WIDTH_SMALL = 180;  // For scale < 0.6
const CARD_GAP = 16; // gap-4 = 16px

const ProductDisplay: React.FC<ProductDisplayProps> = ({
  data,
  ProductCardComponent,
  displayMode,
  scale = 1.0,
  selectedStyleCodes = [],
  onSelectedStyleCodesChange,
  onVisibleProductsChange,
  highlightedStyleCodes,
  onHighlightedStyleCodesChange,
  showAllProducts = false
}) => {
  // Normalised highlight set + toggle helper. Only active when the parent
  // wires up `onHighlightedStyleCodesChange`. We dedupe by the base SKU so
  // a card with color variants is highlighted as one product.
  const highlightEnabled = typeof onHighlightedStyleCodesChange === 'function';
  const normHighlight = useCallback(
    (s?: string | null) => (s ? String(s).toUpperCase().replace(/[^A-Z0-9]/g, '') : ''),
    []
  );
  const highlightedSet = useMemo(() => {
    const s = new Set<string>();
    (highlightedStyleCodes || []).forEach(code => {
      const n = normHighlight(code);
      if (n) s.add(n);
    });
    return s;
  }, [highlightedStyleCodes, normHighlight]);
  const isHighlighted = useCallback((p: ProductItem) => {
    const base = normHighlight(p.base_sku);
    const partial = normHighlight(p.partialSku_OPIM);
    const style = normHighlight(p.attributes?.style_code as string | undefined);
    return highlightedSet.has(base) || highlightedSet.has(partial) || highlightedSet.has(style);
  }, [highlightedSet, normHighlight]);
  const toggleHighlight = useCallback((p: ProductItem) => {
    if (!onHighlightedStyleCodesChange) return;
    const key = normHighlight(p.base_sku) || normHighlight(p.partialSku_OPIM) || normHighlight(p.attributes?.style_code as string | undefined);
    if (!key) return;
    const next = new Set(highlightedSet);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    onHighlightedStyleCodesChange(Array.from(next));
  }, [onHighlightedStyleCodesChange, highlightedSet, normHighlight]);
  // Renders a small top-left checkbox overlay for the highlight UI. We render
  // it as a sibling absolutely-positioned over the card wrapper, so we don't
  // need to touch the card component itself.
  const renderHighlightCheckbox = useCallback((p: ProductItem) => {
    if (!highlightEnabled) return null;
    const checked = isHighlighted(p);
    return (
      <button
        type="button"
        aria-label={checked ? 'Remove from highlight' : 'Add to highlight'}
        title={checked ? 'Highlighted — click to deselect' : 'Highlight this product'}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          toggleHighlight(p);
        }}
        className={
          'absolute top-2 left-2 z-20 w-6 h-6 rounded-md flex items-center justify-center ' +
          'border-2 transition-colors shadow-sm ' +
          (checked
            ? 'bg-purple-600 border-purple-700 text-white hover:bg-purple-700'
            : 'bg-white/95 border-gray-300 text-transparent hover:border-purple-400 hover:text-purple-300')
        }
        style={{ pointerEvents: 'auto' }}
      >
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M5 10.5l3 3 7-7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    );
  }, [highlightEnabled, isHighlighted, toggleHighlight]);

  const rootRef = useRef<HTMLDivElement>(null);
  const prevTopRef = useRef<number | null>(null);
  const isEmpty = !data || !data.products || data.products.length === 0;
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);  // Mobile carousel only
  const [isMobile, setIsMobile] = useState(false);
  const [activeFilters, setActiveFilters] = useState<Record<string, string | null>>({});
  const [sortMode, setSortMode] = useState<SortMode>('default');
  const [isExpanded, setIsExpanded] = useState(false);  // "Show More" expansion state
  
  // Calculate cards per row dynamically based on container width
  const [cardsPerRow, setCardsPerRow] = useState(4); // Default fallback
  
  useEffect(() => {
    const calculateCardsPerRow = () => {
      const container = rootRef.current;
      if (!container) return;
      
      const containerWidth = container.clientWidth;
      // Match CSS grid: repeat(auto-fill, minmax(280px, 1fr)) for normal, 180px for small scale
      const cardMinWidth = scale < 0.6 ? CARD_MIN_WIDTH_SMALL : CARD_MIN_WIDTH_NORMAL;
      
      // Calculate how many cards fit: floor((containerWidth + gap) / (cardWidth + gap))
      const calculatedCardsPerRow = Math.floor((containerWidth + CARD_GAP) / (cardMinWidth + CARD_GAP));
      
      // Ensure at least 1 card per row (no upper limit - fill the row)
      setCardsPerRow(Math.max(1, calculatedCardsPerRow));
    };
    
    // Initial calculation
    calculateCardsPerRow();
    
    // Observe container resize
    const resizeObserver = new ResizeObserver(() => {
      calculateCardsPerRow();
    });
    
    const container = rootRef.current;
    if (container) {
      resizeObserver.observe(container);
    }
    
    return () => {
      resizeObserver.disconnect();
    };
  }, [scale]);
  
  // Initial display limit = 1 row of cards
  const initialDisplayLimit = cardsPerRow;
  const getProductKey = useCallback(
    (p: ProductItem) => p.base_sku || p.partialSku_OPIM || p.attributes?.style_code || p.name,
    []
  );
  const normalizeSku = useCallback(
    (s?: string | null) => (s ? String(s).toUpperCase().replace(/[^A-Z0-9]/g, '') : ''),
    []
  );
  const slugify = useCallback(
    (s?: string | null) => (s ? String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') : ''),
    []
  );
  const buildScrollMeta = useCallback((p: ProductItem) => {
    const baseSku = normalizeSku(p.base_sku);
    const styleCode = normalizeSku(p.attributes?.style_code);
    const partial = normalizeSku(p.partialSku_OPIM || (p as any).partialSku);
    const nameSlug = slugify(p.name);
    const id = `product-card-${baseSku || styleCode || partial || nameSlug || 'product'}`;
    const image = (p.primary_image || (p.color_variants && p.color_variants[0]?.image_url)) || undefined;
    const category =
      p.attributes?.sub_category ||
      p.attributes?.category ||
      (p as any).sub_class_description ||
      (p as any).class_description;
    return { id, partialSku: partial || baseSku || styleCode || '', name: p.name || '', image, category };
  }, [normalizeSku, slugify]);

  /** Wrapper with scroll id + highlight ring — required for chat style-code click-to-scroll. */
  const renderProductCardShell = useCallback(
    (
      p: ProductItem,
      extraClassName: string,
      children: React.ReactNode,
      key?: string
    ) => {
      const scrollMeta = buildScrollMeta(p);
      const highlighted = highlightEnabled && isHighlighted(p);
      return (
        <div
          key={key || p.base_sku || scrollMeta.id}
          id={scrollMeta.id}
          className={
            `product-card-wrapper relative ${extraClassName}` +
            (highlighted ? ' ring-2 ring-purple-500 ring-offset-1 rounded-lg' : '')
          }
          data-partial-sku={scrollMeta.partialSku}
          data-product-name={scrollMeta.name}
          data-product-image={scrollMeta.image || ''}
          data-product-category={scrollMeta.category || ''}
        >
          {renderHighlightCheckbox(p)}
          {children}
        </div>
      );
    },
    [buildScrollMeta, highlightEnabled, isHighlighted, renderHighlightCheckbox]
  );

  // Reset expansion when new data arrives
  useEffect(() => {
    setIsExpanded(false);
  }, [data.products, data.heading]);
  
  // Generate fallback facets from product attributes if backend didn't provide them
  const effectiveFacets = useMemo(() => {
    // Provenance toggles (Made by UniFirst, Catalogue) mirror the backend's
    // collaborator_api._augment_facets so Chat mode shows the same high-signal
    // pills even when the chat product pipeline didn't emit them. Idempotent:
    // skips a facet if one with the same field_name already exists.
    const addProvenanceFacets = (baseFacets: typeof data.facets) => {
      const facets = [...(baseFacets || [])];
      if (!data.products || data.products.length < 2) return facets;
      let nextId = facets.length + 1;
      const styleOf = (p: ProductItem) => p.attributes?.style_code || p.base_sku || '';

      if (!facets.some(f => f.field_name === 'made_by_unifirst')) {
        const yes: string[] = [];
        const no: string[] = [];
        data.products.forEach(p => {
          const style = styleOf(p);
          if (!style) return;
          const mbu = (p.attributes as any)?.made_by_unifirst === true
            || (p.attributes as any)?.made_by_unifirst === 'Yes';
          (mbu ? yes : no).push(style);
        });
        if (yes.length > 0 && no.length > 0) {
          facets.push({
            id: `pf${nextId++}`,
            facet_type: 'categorical',
            field_name: 'made_by_unifirst',
            display_name: 'Made by UniFirst',
            values: ['UniFirst Made', 'Other Vendors'],
            style_codes_by_value: { 'UniFirst Made': yes, 'Other Vendors': no },
          });
        }
      }

      if (!facets.some(f => f.field_name === 'is_catalog_item')) {
        const yes: string[] = [];
        const no: string[] = [];
        data.products.forEach(p => {
          const style = styleOf(p);
          if (!style) return;
          const attrs = p.attributes as any;
          const isCatalog = Boolean(
            (p as any).is_catalog_item
            || attrs?.is_catalog_item
            || String(attrs?.industry || '').trim()
            || (p as any).source === 'detailed_product_catalog'
          );
          (isCatalog ? yes : no).push(style);
        });
        if (yes.length > 0 && no.length > 0) {
          facets.push({
            id: `pf${nextId++}`,
            facet_type: 'categorical',
            field_name: 'is_catalog_item',
            display_name: 'Catalogue',
            values: ['In Catalogue', 'CPD Only'],
            style_codes_by_value: { 'In Catalogue': yes, 'CPD Only': no },
          });
        }
      }

      return facets;
    };

    // If backend provided facets, use them (plus provenance toggles)
    if (data.facets && data.facets.length > 0) {
      console.log('✅ Using backend-provided facets:', data.facets.length);
      return addProvenanceFacets(data.facets);
    }
    
    // Otherwise, try to generate facets from product attributes
    if (!data.products || data.products.length < 2) {
      return [];
    }
    
    console.log('⚠️ No backend facets, generating from product attributes...');
    const generatedFacets: typeof data.facets = [];
    let facetIndex = 0;
    
    // Fields to check for facet generation
    const facetableFields = [
      { field: 'attributes.parent_brand', displayName: 'Brand', type: 'categorical' as const },
      { field: 'attributes.gender', displayName: 'Gender', type: 'categorical' as const },
      { field: 'attributes.product_sub_type_category', displayName: 'Type', type: 'categorical' as const },
      { field: 'attributes.vendorname', displayName: 'Vendor', type: 'categorical' as const },
    ];
    
    // Also check for safety features
    const safetyFields = [
      { field: 'badges', displayName: 'Features', type: 'key_feature' as const },
    ];
    
    // Helper to get nested field value
    const getFieldValue = (product: ProductItem, fieldPath: string): string | undefined => {
      const parts = fieldPath.split('.');
      let value: any = product;
      for (const part of parts) {
        if (value == null) return undefined;
        value = value[part];
      }
      return typeof value === 'string' ? value : undefined;
    };
    
    // Generate facets from categorical fields
    for (const { field, displayName, type } of facetableFields) {
      const valuesMap: Record<string, string[]> = {};
      
      data.products.forEach(p => {
        const value = getFieldValue(p, field);
        if (value && value.trim()) {
          const styleCode = p.attributes?.style_code || p.base_sku || '';
          if (!valuesMap[value]) {
            valuesMap[value] = [];
          }
          if (styleCode && !valuesMap[value].includes(styleCode)) {
            valuesMap[value].push(styleCode);
          }
        }
      });
      
      const uniqueValues = Object.keys(valuesMap);
      if (uniqueValues.length >= 2 && uniqueValues.length <= 10) {
        facetIndex++;
        generatedFacets.push({
          id: `f${facetIndex}`,
          facet_type: type,
          field_name: field.replace('attributes.', ''),
          display_name: displayName,
          values: uniqueValues,
          style_codes_by_value: valuesMap
        });
      }
    }

    // COLOR FACET - Built from color_variants, displayed as "Name (code)"
    const colorValuesMap: Record<string, string[]> = {};
    data.products.forEach(p => {
      const styleCode = p.attributes?.style_code || p.base_sku || '';
      if (!styleCode || !p.color_variants?.length) return;
      for (const v of p.color_variants) {
        const name = v.color_name?.trim();
        const code = v.color_code?.trim();
        if (!name) continue;
        const label = code ? `${name} (${code})` : name;
        if (!colorValuesMap[label]) colorValuesMap[label] = [];
        if (!colorValuesMap[label].includes(styleCode)) {
          colorValuesMap[label].push(styleCode);
        }
      }
    });
    const colorValues = Object.keys(colorValuesMap);
    if (colorValues.length >= 2) {
      facetIndex++;
      generatedFacets.push({
        id: `f${facetIndex}`,
        facet_type: 'categorical',
        field_name: 'color_variant',
        display_name: 'Color',
        values: colorValues.slice(0, 15),
        style_codes_by_value: colorValuesMap
      });
    }
    
    // =========================================================================
    // SIZE FACET - Extract size categories from complex size strings
    // =========================================================================
    // Sizes can be complex: "XS–4XL Regular; M–4XL Tall", "Waist 30–50; inseam 13""
    // Extract key size categories for faceting
    const sizeCategories: Record<string, string[]> = {};
    
    data.products.forEach(p => {
      const sizes = p.attributes?.sizes || (p as any).sizes;
      if (!sizes || typeof sizes !== 'string') return;
      
      const styleCode = p.attributes?.style_code || p.base_sku || '';
      const sizeLower = sizes.toLowerCase();
      
      // Extract size categories
      // Tall sizing
      if (sizeLower.includes('tall')) {
        if (!sizeCategories['Tall']) sizeCategories['Tall'] = [];
        if (styleCode && !sizeCategories['Tall'].includes(styleCode)) {
          sizeCategories['Tall'].push(styleCode);
        }
      }
      // Regular sizing
      if (sizeLower.includes('regular') || sizeLower.includes('reg')) {
        if (!sizeCategories['Regular']) sizeCategories['Regular'] = [];
        if (styleCode && !sizeCategories['Regular'].includes(styleCode)) {
          sizeCategories['Regular'].push(styleCode);
        }
      }
      // Short/Petite sizing  
      if (sizeLower.includes('short') || sizeLower.includes('petite')) {
        if (!sizeCategories['Short/Petite']) sizeCategories['Short/Petite'] = [];
        if (styleCode && !sizeCategories['Short/Petite'].includes(styleCode)) {
          sizeCategories['Short/Petite'].push(styleCode);
        }
      }
      // Big sizing
      if (sizeLower.includes('big')) {
        if (!sizeCategories['Big']) sizeCategories['Big'] = [];
        if (styleCode && !sizeCategories['Big'].includes(styleCode)) {
          sizeCategories['Big'].push(styleCode);
        }
      }
      // Extended sizes (5XL, 6XL)
      if (sizeLower.includes('5xl') || sizeLower.includes('6xl')) {
        if (!sizeCategories['Extended (5XL+)']) sizeCategories['Extended (5XL+)'] = [];
        if (styleCode && !sizeCategories['Extended (5XL+)'].includes(styleCode)) {
          sizeCategories['Extended (5XL+)'].push(styleCode);
        }
      }
      // Inseam
      const inseamMatch = sizes.match(/inseam\s*(\d+)/i);
      if (inseamMatch) {
        const inseamLabel = `${inseamMatch[1]}" Inseam`;
        if (!sizeCategories[inseamLabel]) sizeCategories[inseamLabel] = [];
        if (styleCode && !sizeCategories[inseamLabel].includes(styleCode)) {
          sizeCategories[inseamLabel].push(styleCode);
        }
      }
    });
    
    // Only create size facet if we have meaningful distinctions
    const sizeValues = Object.keys(sizeCategories).filter(k => sizeCategories[k].length > 0);
    if (sizeValues.length >= 2) {
      facetIndex++;
      generatedFacets.push({
        id: `f${facetIndex}`,
        facet_type: 'categorical' as const,
        field_name: 'sizes',
        display_name: 'Size Options',
        values: sizeValues,
        style_codes_by_value: sizeCategories
      });
    }
    
    // Check for collection info - create "collection" pseudo-facet
    const collectionIds = new Set<string>();
    data.products.forEach(p => {
      if (p.is_collection && p.collection_id) {
        collectionIds.add(p.collection_id);
      }
    });
    
    console.log('📦 Generated fallback facets:', generatedFacets.length, generatedFacets.map(f => f.field_name));
    return addProvenanceFacets(generatedFacets);
  }, [data.facets, data.products]);

  // DEBUG: Log facets received - expanded logging
  useEffect(() => {
    console.log('🎨 ProductDisplay received:', {
      heading: data.heading,
      productCount: data.products?.length,
      hasFacets: !!data.facets,
      facetCount: data.facets?.length || 0,
      effectiveFacetCount: effectiveFacets.length,
      facetTypes: data.facets?.map(f => f.facet_type),
      facetFieldNames: data.facets?.map(f => f.field_name),
      facetValueCounts: data.facets?.map(f => ({ field: f.field_name, valueCount: f.values?.length || 0 })),
      rawFacets: data.facets,  // Full facet data for debugging
      willRenderToolbar: effectiveFacets.length > 0 || data.products.some(p => p.is_collection)
    });
  }, [data.heading, data.products?.length, data.facets, effectiveFacets]);

  // Handle multi-facet filter changes
  const handleFilterChange = useCallback((filters: Record<string, string | null>) => {
    setActiveFilters(filters);
  }, []);

  // Handle sort mode changes
  const handleSortChange = useCallback((mode: SortMode) => {
    setSortMode(mode);
  }, []);

  // Extract active color filter for auto-selecting color variants on cards
  const activeColorFilter = useMemo(() => {
    const raw = activeFilters['color_variant'] || activeFilters['color_keywords'] || activeFilters['color'] || null;
    if (!raw) return null;
    // Strip "(code)" suffix so the card matcher can compare by color name
    const match = raw.match(/^(.+?)\s*\(\w+\)$/);
    return match ? match[1].trim() : raw;
  }, [activeFilters]);

  // Filter products based on all active facet filters
  const filteredProducts = useMemo(() => {
    let products = [...data.products];
    const normalizedSelected = selectedStyleCodes.map(code => code.toUpperCase().replace(/[^A-Z0-9]/g, ''));

    // Apply style-code selections first (acts like a "selected items" filter)
    if (normalizedSelected.length > 0) {
      const selectedSet = new Set(normalizedSelected);
      products = products.filter(p => {
        const base = (p.base_sku || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
        const partial = (p.partialSku_OPIM || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
        const styleCode = (p.attributes?.style_code || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
        return selectedSet.has(base) || selectedSet.has(partial) || selectedSet.has(styleCode);
      });
    }
    
    // Apply facet filters
    if (effectiveFacets.length > 0 && Object.keys(activeFilters).length > 0) {
      const activeFilterEntries = Object.entries(activeFilters).filter(([_, value]) => value !== null);
      
      if (activeFilterEntries.length > 0) {
        let allowedSkus: string[] | null = null;
        
        for (const [fieldName, filterValue] of activeFilterEntries) {
          if (!filterValue) continue;
          
          const facet = effectiveFacets.find(f => f.field_name === fieldName);
          if (!facet) continue;
          
          const skusForValue = facet.style_codes_by_value[filterValue] || [];
          
          if (allowedSkus === null) {
            allowedSkus = [...skusForValue];
          } else {
            const skuSet = new Set(skusForValue);
            allowedSkus = allowedSkus.filter(sku => skuSet.has(sku));
          }
        }
        
        if (!allowedSkus || allowedSkus.length === 0) {
          return [];
        }
        
        const allowedSet = new Set(allowedSkus);
        products = products.filter(p => 
          allowedSet.has(p.base_sku) || 
          allowedSet.has(p.partialSku_OPIM || '') ||
          allowedSet.has(p.attributes?.style_code || '')
        );
      }
    }
    
    // Apply sorting
    if (sortMode === 'collection') {
      // Group by collection, then standalone items
      const collections: Map<string, ProductItem[]> = new Map();
      const standalone: ProductItem[] = [];
      
      products.forEach(p => {
        if (p.is_collection && p.collection_id) {
          if (!collections.has(p.collection_id)) {
            collections.set(p.collection_id, []);
          }
          collections.get(p.collection_id)!.push(p);
        } else {
          standalone.push(p);
        }
      });
      
      // Flatten: collections first (sorted by best score in collection), then standalone
      const sortedProducts: ProductItem[] = [];
      
      // Sort collections by their best scoring product
      const collectionEntries = Array.from(collections.entries());
      collectionEntries.sort((a, b) => {
        const aScore = Math.max(...a[1].map(p => p.score?.total_score ?? 0));
        const bScore = Math.max(...b[1].map(p => p.score?.total_score ?? 0));
        return bScore - aScore;
      });
      
      // Add collection products (sorted by score within each collection)
      collectionEntries.forEach(([_, collProducts]) => {
        collProducts.sort((a, b) => (b.score?.total_score ?? 0) - (a.score?.total_score ?? 0));
        sortedProducts.push(...collProducts);
      });
      
      // Add standalone products
      sortedProducts.push(...standalone);
      
      return sortedProducts;
    } else if (sortMode === 'price_low') {
      return products.sort((a, b) => {
        // Handle price as string or number, also check std_cost in attributes
        const getPrice = (p: ProductItem): number => {
          if (typeof p.price === 'number') return p.price;
          if (typeof p.price === 'string') return parseFloat(p.price.replace(/[^0-9.]/g, '') || '0');
          // Fallback to std_cost from attributes
          const stdCost = (p.attributes as Record<string, unknown>)?.std_cost;
          if (typeof stdCost === 'number') return stdCost;
          if (typeof stdCost === 'string') return parseFloat(stdCost.replace(/[^0-9.]/g, '') || '0');
          return 0;
        };
        return getPrice(a) - getPrice(b);
      });
    } else if (sortMode === 'price_high') {
      return products.sort((a, b) => {
        // Handle price as string or number, also check std_cost in attributes
        const getPrice = (p: ProductItem): number => {
          if (typeof p.price === 'number') return p.price;
          if (typeof p.price === 'string') return parseFloat(p.price.replace(/[^0-9.]/g, '') || '0');
          // Fallback to std_cost from attributes
          const stdCost = (p.attributes as Record<string, unknown>)?.std_cost;
          if (typeof stdCost === 'number') return stdCost;
          if (typeof stdCost === 'string') return parseFloat(stdCost.replace(/[^0-9.]/g, '') || '0');
          return 0;
        };
        return getPrice(b) - getPrice(a);
      });
    } else if (sortMode === 'weight_high' || sortMode === 'weight_low') {
      // Extract weight in oz from attributes or differentiating features
      const getWeight = (product: ProductItem): number => {
        // Check attributes for weight - cast to any for flexible field access
        const attrs = (product.attributes || {}) as Record<string, unknown>;
        const longDesc = (attrs.long_description as string) || (attrs.description as string) || '';
        const fabricDesc = (attrs.fabric_description as string) || '';
        const mozartDesc = (attrs.Mozart_Desc as string) || '';
        
        // Also check reasons (differentiating features)
        const features = product.reasons || [];
        const allText = `${longDesc} ${fabricDesc} ${mozartDesc} ${features.join(' ')}`;
        
        // Extract weight pattern: "7.5 oz", "9oz", "4.5 OZ", etc.
        const weightMatch = allText.match(/(\d+(?:\.\d+)?)\s*oz/i);
        if (weightMatch) {
          return parseFloat(weightMatch[1]);
        }
        
        // Default to middle weight if not found
        return sortMode === 'weight_high' ? -1 : 999;
      };
      
      return products.sort((a, b) => {
        const aWeight = getWeight(a);
        const bWeight = getWeight(b);
        return sortMode === 'weight_high' 
          ? bWeight - aWeight  // Heavy → Light (descending)
          : aWeight - bWeight; // Light → Heavy (ascending)
      });
    }
    
    return products;
  }, [data.products, effectiveFacets, activeFilters, sortMode, selectedStyleCodes]);

  // Limit displayed products based on expansion state
  // Facets are still calculated from ALL filteredProducts for full filtering capability
  const displayedProducts = useMemo(() => {
    if (showAllProducts || isExpanded || filteredProducts.length <= initialDisplayLimit) {
      return filteredProducts;
    }
    return filteredProducts.slice(0, initialDisplayLimit);
  }, [filteredProducts, isExpanded, initialDisplayLimit, showAllProducts]);

  // Notify parent (Collaborator) of the post-facet/post-sort visible set so it
  // can hand the user's curated list straight to Inquire without re-searching.
  useEffect(() => {
    if (onVisibleProductsChange) {
      onVisibleProductsChange(filteredProducts);
    }
  }, [filteredProducts, onVisibleProductsChange]);

  // Check if there are more products to show
  const hasMoreProducts = !showAllProducts && filteredProducts.length > initialDisplayLimit && !isExpanded;
  const hiddenProductCount = Math.max(filteredProducts.length - initialDisplayLimit, 0);

  // Track which products are currently displayed (for grouped views)
  const displayedProductKeys = useMemo(() => {
    const keys = new Set<string>();
    displayedProducts.forEach((p) => {
      const key = getProductKey(p);
      if (key) {
        keys.add(key);
      }
    });
    return keys;
  }, [displayedProducts, getProductKey]);

  // Get collection groups for visual separation when sorting by collection
  const collectionGroups = useMemo(() => {
    if (sortMode !== 'collection') return null;
    
    const groups: { 
      collectionId: string | null; 
      products: ProductItem[]; 
      glowColor?: string;
      sharedDescription?: string;  // Shared long_description for the collection
    }[] = [];
    let currentGroup: { 
      collectionId: string | null; 
      products: ProductItem[]; 
      glowColor?: string;
      sharedDescription?: string;
    } | null = null;
    
    displayedProducts.forEach(product => {
      // Normalize collection ID: undefined becomes null
      const collId: string | null = (product.is_collection && product.collection_id) ? product.collection_id : null;
      
      if (!currentGroup || currentGroup.collectionId !== collId) {
        currentGroup = {
          collectionId: collId,
          products: [],
          glowColor: product.collection_glow_color,
          sharedDescription: product.collection_shared_description
        };
        groups.push(currentGroup);
      }
      currentGroup!.products.push(product);
    });
    
    return groups;
  }, [displayedProducts, sortMode]);

  // Determine if we should use detailed cards
  // TEMPORARILY DISABLED: Always use standard cards
  // TODO: Re-enable detailed cards when ready - the original logic was:
  // - Never on mobile
  // - Use explicit displayMode if provided
  // - Fallback: use detailed for 2 or fewer products on tablet/desktop
  const useDetailedCards = useMemo(() => {
    // DISABLED: Always use standard cards for now
    console.log('🎨 Detailed cards DISABLED - using standard cards');
    return false;
    
    /* ORIGINAL LOGIC - uncomment to re-enable:
    // Never use detailed cards on mobile
    if (isMobile) {
      console.log('📱 Mobile detected - forcing standard cards');
      return false;
    }
    
    // Use explicit display mode if provided (from message-level state)
    if (displayMode) {
      console.log(`🎨 Using explicit displayMode: ${displayMode} (${displayMode === 'detailed' ? 'detailed cards' : 'standard cards'})`);
      return displayMode === 'detailed';
    }
    
    // Fallback: calculate based on product count (2 or fewer = detailed)
    const shouldUseDetailed = data.products.length <= 2;
    console.log(`🎨 Fallback calculation: ${data.products.length} products -> ${shouldUseDetailed ? 'detailed' : 'standard'} cards`);
    return shouldUseDetailed;
    */
  }, [displayMode, data.products.length, isMobile]);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle carousel scroll on mobile
  useEffect(() => {
    if (!isMobile || useDetailedCards || !carouselRef.current) return;

    const carousel = carouselRef.current;
    const handleScroll = () => {
      const scrollLeft = carousel.scrollLeft;
      const cardWidth = carousel.offsetWidth;
      const newIndex = Math.round(scrollLeft / cardWidth);
      setCurrentIndex(newIndex);
    };

    carousel.addEventListener('scroll', handleScroll);
    return () => carousel.removeEventListener('scroll', handleScroll);
  }, [isMobile, useDetailedCards]);

  // Scroll to specific card (for dot navigation)
  const scrollToIndex = (index: number) => {
    if (carouselRef.current) {
      const cardWidth = carouselRef.current.offsetWidth;
      carouselRef.current.scrollTo({
        left: cardWidth * index,
        behavior: 'smooth'
      });
    }
  };

  // Log data for debugging - removed for production
  useEffect(() => {
    if (!data || !data.products || data.products.length === 0) {
      console.warn('ProductDisplay received invalid or empty data');
      return;
    }

    // Check for missing images - this is important to keep for debugging
    const missingImages = data.products.filter(p => !p.primary_image && 
      (!p.color_variants || p.color_variants.every(v => !v.image_url)));
    
    if (missingImages.length > 0) {
      console.warn(`${missingImages.length} products have no images`);
    }
  }, [data]);

  // Validate data
  // Note: We still render a container so layout effects run consistently

  // Helper function to render products by category group
  const renderProductsByCategory = (group: CategoryGroup) => {
    const groupProducts = group.product_indices
      .map(idx => data.products[idx])
      .filter((p): p is ProductItem => {
        if (!p) return false;
        const key = getProductKey(p);
        return key ? displayedProductKeys.has(key) : false;
      });

    if (groupProducts.length === 0) return null;
    
    return (
      <div key={group.category} className="category-group mb-6">
        {/* Category Header */}
        <div className="category-header mb-3 pb-2 border-b-2 border-teal-600">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            {group.icon && <span className="text-2xl">{group.icon}</span>}
            <span>{group.label}</span>
            <span className="text-sm font-normal text-gray-500">
              ({groupProducts.length}{groupProducts.length !== group.count ? ` of ${group.count}` : ''})
            </span>
          </h3>
        </div>
        
        {/* Products in this category */}
        {useDetailedCards ? (
          <div className="flex flex-col gap-4">
            {groupProducts.map((p) => (
              (() => {
                const scrollMeta = buildScrollMeta(p);
                return (
                  <div
                    key={p.base_sku || Math.random().toString()}
                    id={scrollMeta.id}
                    className="product-card-wrapper"
                    data-partial-sku={scrollMeta.partialSku}
                    data-product-name={scrollMeta.name}
                    data-product-image={scrollMeta.image || ''}
                    data-product-category={scrollMeta.category || ''}
                  >
                    <ProductCardDetailed product={p} />
                  </div>
                );
              })()
            ))}
          </div>
        ) : (
          <div className="relative w-full">
            {/* Mobile: Horizontal Carousel per category */}
            <div className="md:hidden">
              <div
                className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar"
                style={{
                  scrollSnapType: 'x mandatory',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}
              >
                {groupProducts.map((p) => (
                  (() => {
                    const scrollMeta = buildScrollMeta(p);
                    return (
                      <div
                        key={p.base_sku || Math.random().toString()}
                        id={scrollMeta.id}
                        className="flex-shrink-0 w-full snap-center px-2 product-card-wrapper"
                        data-partial-sku={scrollMeta.partialSku}
                        data-product-name={scrollMeta.name}
                        data-product-image={scrollMeta.image || ''}
                        data-product-category={scrollMeta.category || ''}
                      >
                        <ProductCardComponent product={p} scale={scale} activeColorFilter={activeColorFilter} />
                      </div>
                    );
                  })()
                ))}
              </div>
            </div>

            {/* Tablet and Desktop: Vertical Scroll Grid per category */}
            <div className="hidden md:block">
              <div
                className="grid gap-4"
                style={{
                  gridTemplateColumns: scale < 0.6 
                    ? 'repeat(auto-fill, minmax(180px, 1fr))' 
                    : 'repeat(auto-fill, minmax(280px, 1fr))',
                  maxWidth: '100%'
                }}
              >
                {groupProducts.map((p) => (
                  (() => {
                    const scrollMeta = buildScrollMeta(p);
                    return (
                      <div
                        key={p.base_sku || Math.random().toString()}
                        id={scrollMeta.id}
                        className="product-card-wrapper"
                        data-partial-sku={scrollMeta.partialSku}
                        data-product-name={scrollMeta.name}
                        data-product-image={scrollMeta.image || ''}
                        data-product-category={scrollMeta.category || ''}
                      >
                        <ProductCardComponent product={p} scale={scale} activeColorFilter={activeColorFilter} />
                      </div>
                    );
                  })()
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Calculate scale-based font sizes (no CSS transform - causes blurriness)
  const headingSize = scale < 1 ? 'text-base' : 'text-lg';

  // Preserve scroll position relative to message when layout height changes
  useLayoutEffect(() => {
    const currentTop = rootRef.current?.getBoundingClientRect().top ?? null;
    if (prevTopRef.current !== null && currentTop !== null) {
      const delta = currentTop - prevTopRef.current;
      if (Math.abs(delta) > 1) {
        window.scrollBy(0, delta);
      }
    }
    prevTopRef.current = currentTop;
  }, [displayedProducts.length, isExpanded, activeFilters, selectedStyleCodes, data.heading, data.products.length]);

  if (isEmpty) {
    return (
      <div
        ref={rootRef}
        className="product-display w-full mt-2 mb-4"
      />
    );
  }

  return (
    <div 
      ref={rootRef}
      className="product-display w-full mt-2 mb-4"
    >
      {/* Display heading if provided */}
      {data.heading && (
        <h3 className={`${headingSize} font-semibold text-gray-800 mb-3 flex items-center gap-2`}>
          <span className="w-1 h-5 bg-teal-500 rounded-full"></span>
          {data.heading}
        </h3>
      )}
      
      {/* Product filter toolbar with facets and collection sorting */}
      {/* Hide toolbar in compact mode (for IRIS, docs, etc.) */}
      {!data.compact && (effectiveFacets.length > 0 || data.products.some(p => p.is_collection)) && (
        <ProductFilterToolbar 
          facets={effectiveFacets}
          products={data.products}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
          activeFilters={activeFilters}
          currentSort={sortMode}
          totalCount={data.products.length}
          filteredCount={filteredProducts.length}
        />
      )}

      {/* Selected style code chips */}
      {selectedStyleCodes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {selectedStyleCodes.map(code => (
            <button
              key={code}
              type="button"
              className="inline-flex items-center gap-2 px-3 py-1 text-xs font-semibold rounded-full bg-teal-50 text-teal-700 border border-teal-200 hover:bg-teal-100"
              onClick={() => {
                if (onSelectedStyleCodesChange) {
                  onSelectedStyleCodesChange(selectedStyleCodes.filter(c => c !== code));
                }
              }}
            >
              <span>{code}</span>
              <span className="text-teal-500">✕</span>
            </button>
          ))}
          {onSelectedStyleCodesChange && (
            <button
              type="button"
              className="text-xs text-gray-500 hover:text-gray-700 underline"
              onClick={() => onSelectedStyleCodesChange([])}
            >
              Clear selection
            </button>
          )}
        </div>
      )}
      
      {/* Product cards display - no header needed as LLM text includes "Found X products" */}
      {/* Check if we have category grouping */}
      {data.grouped && data.category_groups && data.category_groups.length > 0 ? (
        // Render products grouped by category
        <>
          <div className="category-grouped-display">
            {data.category_groups.map(group => renderProductsByCategory(group))}
          </div>
          {hasMoreProducts && (
            <div className="flex justify-center mt-6">
              <button
                data-product-show-more="true"
                onClick={() => setIsExpanded(true)}
                className="px-8 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 
                           text-white font-semibold rounded-xl shadow-md 
                           hover:from-teal-600 hover:to-cyan-600 hover:shadow-lg hover:scale-[1.02]
                           transition-all duration-200 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Show {hiddenProductCount} More Products
              </button>
            </div>
          )}
        </>
      ) : sortMode === 'collection' && collectionGroups ? (
        // Collection-grouped view
        <div className="collection-grouped-display space-y-6">
          {collectionGroups.map((group, groupIndex) => (
            <div key={group.collectionId || `standalone-${groupIndex}`} className="collection-group">
              {/* Collection header */}
              {group.collectionId && (
                <div 
                  className="collection-header mb-3 pb-2 flex items-center gap-2"
                  style={{ borderBottom: `2px solid ${group.glowColor || '#3b82f6'}` }}
                >
                  <span 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: group.glowColor || '#3b82f6' }}
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Product Variant Collection
                  </span>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {group.products.length} variants
                  </span>
                </div>
              )}
              {!group.collectionId && groupIndex > 0 && (
                <div className="standalone-header mb-3 pb-2 border-b border-gray-200">
                  <span className="text-sm font-medium text-gray-500">
                    Individual Products
                  </span>
                </div>
              )}
              
              {/* Products in this group */}
              {useDetailedCards ? (
                <div className="flex flex-col gap-4">
                  {group.products.map((p) => (
                    <ProductCardDetailed key={p.base_sku || Math.random().toString()} product={p} />
                  ))}
                </div>
              ) : (
                <div className="relative w-full">
                  {/* Mobile: Horizontal Carousel per collection */}
                  <div className="md:hidden">
                    <div
                      className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar"
                      style={{
                        scrollSnapType: 'x mandatory',
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none'
                      }}
                    >
                      {group.products.map((p) =>
                        renderProductCardShell(
                          p,
                          'flex-shrink-0 w-full snap-center px-2',
                          <ProductCardComponent product={p} scale={scale} activeColorFilter={activeColorFilter} />
                        )
                      )}
                    </div>
                  </div>

                  {/* Tablet and Desktop: Grid per collection */}
                  <div className="hidden md:block">
                    <div className="flex gap-4">
                      {/* Product cards grid */}
                      <div
                        className="grid gap-4 flex-grow"
                        style={{
                          gridTemplateColumns: scale < 0.6 
                            ? 'repeat(auto-fill, minmax(180px, 1fr))' 
                            : 'repeat(auto-fill, minmax(280px, 1fr))',
                          maxWidth: group.sharedDescription ? '75%' : '100%'
                        }}
                      >
                        {group.products.map((p) =>
                          renderProductCardShell(
                            p,
                            '',
                            <ProductCardComponent product={p} scale={scale} activeColorFilter={activeColorFilter} />
                          )
                        )}
                      </div>
                      
                      {/* Shared Description Panel (right side) */}
                      {group.collectionId && group.sharedDescription && (
                        <div 
                          className="flex-shrink-0 w-1/4 min-w-[200px] max-w-[300px] p-4 rounded-lg"
                          style={{ 
                            backgroundColor: `${group.glowColor || '#3b82f6'}10`,
                            borderLeft: `3px solid ${group.glowColor || '#3b82f6'}`
                          }}
                        >
                          <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            Shared Description
                          </h4>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {group.sharedDescription}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
          
          {/* Show More Button for collection view */}
          {hasMoreProducts && (
            <div className="flex justify-center mt-6">
              <button
                data-product-show-more="true"
                onClick={() => setIsExpanded(true)}
                className="px-8 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 
                           text-white font-semibold rounded-xl shadow-md 
                           hover:from-teal-600 hover:to-cyan-600 hover:shadow-lg hover:scale-[1.02]
                           transition-all duration-200 flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Show {hiddenProductCount} More Products
              </button>
            </div>
          )}
        </div>
      ) : useDetailedCards ? (
        // Regular detailed view (no grouping)
        <div className="flex flex-col gap-4">
          {displayedProducts.map((p) => (
            <ProductCardDetailed key={p.base_sku || Math.random().toString()} product={p} />
          ))}
          
          {/* Show More Button */}
          {hasMoreProducts && (
            <button
              data-product-show-more="true"
              onClick={() => setIsExpanded(true)}
              className="mx-auto mt-4 px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 
                         text-white font-medium rounded-xl shadow-md 
                         hover:from-teal-600 hover:to-cyan-600 hover:shadow-lg
                         transition-all duration-200 flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              Show {hiddenProductCount} More Products
            </button>
          )}
        </div>
      ) : (
        // Regular grid view (no grouping)
        <div className="relative w-full">
          {/* Mobile: Horizontal Carousel */}
          <div className="md:hidden">
            <div
              ref={carouselRef}
              className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar"
              style={{
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}
            >
              {displayedProducts.map((p) =>
                renderProductCardShell(
                  p,
                  'flex-shrink-0 w-full snap-center px-2',
                  <ProductCardComponent product={p} scale={scale} activeColorFilter={activeColorFilter} />
                )
              )}
            </div>
            
            {/* Dot Indicators */}
            {displayedProducts.length > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {displayedProducts.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => scrollToIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${
                      index === currentIndex 
                        ? 'bg-teal-600 w-6' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Go to product ${index + 1}`}
                  />
                ))}
              </div>
            )}
            
            {/* Mobile: Show More Button */}
            {hasMoreProducts && (
              <button
                data-product-show-more="true"
                onClick={() => setIsExpanded(true)}
                className="w-full mt-4 px-4 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 
                           text-white font-medium rounded-xl shadow-md 
                           hover:from-teal-600 hover:to-cyan-600 hover:shadow-lg
                           transition-all duration-200 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Show {hiddenProductCount} More
              </button>
            )}
          </div>

          {/* Tablet and Desktop: Vertical Scroll Grid */}
          <div className="hidden md:block">
            <div
              className="grid gap-4"
              style={{
                gridTemplateColumns: scale < 0.6 
                  ? 'repeat(auto-fill, minmax(180px, 1fr))' 
                  : 'repeat(auto-fill, minmax(280px, 1fr))',
                maxWidth: '100%'
              }}
            >
              {displayedProducts.map((p) =>
                renderProductCardShell(
                  p,
                  '',
                  <ProductCardComponent product={p} scale={scale} activeColorFilter={activeColorFilter} />
                )
              )}
            </div>
            
            {/* Desktop: Show More Button */}
            {hasMoreProducts && (
              <div className="flex justify-center mt-6">
                <button
                  data-product-show-more="true"
                  onClick={() => setIsExpanded(true)}
                  className="px-8 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 
                             text-white font-semibold rounded-xl shadow-md 
                             hover:from-teal-600 hover:to-cyan-600 hover:shadow-lg hover:scale-[1.02]
                             transition-all duration-200 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  Show {hiddenProductCount} More Products
                </button>
              </div>
            )}
          </div>
          
          {/* CSS for mobile carousel and scrollbars */}
          <style>{`
            .hide-scrollbar::-webkit-scrollbar {
              display: none;
            }
            
            /* Thin scrollbar for product card content */
            .overflow-y-auto::-webkit-scrollbar {
              width: 4px;
            }
            .overflow-y-auto::-webkit-scrollbar-track {
              background: transparent;
            }
            .overflow-y-auto::-webkit-scrollbar-thumb {
              background: #cfd2d6;
              border-radius: 2px;
            }
            .overflow-y-auto::-webkit-scrollbar-thumb:hover {
              background: #9da2a8;
            }
          `}</style>
        </div>
      )}
      
      {data.clarifying_questions && data.clarifying_questions.length > 0 && (
        <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <h4 className="font-medium text-gray-700 mb-2">Clarifying Questions:</h4>
          <ul className="list-disc list-inside text-sm text-gray-600">
            {data.clarifying_questions.map((q: string, i: number) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ProductDisplay;